import divide from 'lodash/divide'
import every from 'lodash/every'
import filter from 'lodash/filter'
import find from 'lodash/find'
import get from 'lodash/get'
import indexOf from 'lodash/indexOf'
import size from 'lodash/size'
import slice from 'lodash/slice'
import some from 'lodash/some'
import {createSelector} from 'reselect'

import * as fitProfile from './fitProfile'
import {status} from './helpers'
import * as URLS from '../urls'
import {isFilledString} from '../utils'

// Other

export const getStepId = (step) => get(step, 'id')
export const getStepSlug = (step) => get(step, 'slug')
export const getStepUrl = (step) =>
  ((slug) => (slug ? `${URLS.FIT}/${slug}` : undefined))(getStepSlug(step))
export const stepHasValidResponse = (step, responses) =>
  isFilledString(get(responses, getStepId(step)))
export const stepsHaveValidResponses = (steps, responses) =>
  every(steps, (step) => stepHasValidResponse(step, responses))
export const stepIsInSurvey = (step) => !!get(step, 'flags.IS_SURVEY')
export const stepIsRequired = (step) => !!get(step, 'flags.IS_REQUIRED')
export const filterSurveySteps = (steps) => filter(steps, stepIsInSurvey)
export const filterRequiredSteps = (steps) => filter(steps, stepIsRequired)
export const findById = (x, id) => find(x, {id})
export const findBySlug = (x, slug) => find(x, {slug})
export const hasSlug = (x, slug) => some(findBySlug(x, slug))
export const wrap = (subState) => ({fit: subState})

// Get

export const currStepSlug = (state) => get(state, 'fit.currStepSlug')
export const error = (state) => get(state, 'fit.error')
export const fit = (state) => get(state, 'fit')
export const flow = (state) => get(state, 'fit.flow')
export const info = (state) => get(state, 'fit.info')
export const isEditing = (state) => get(state, 'fit.isEditing')
export const oldStepSlug = (state) => get(state, 'fit.oldStepSlug')
export const onDeckStepSlug = (state) => get(state, 'fit.onDeckStepSlug')
export const sizeInputsById = (state) => get(state, 'fit.sizeInputsById')
export const stepInputsById = (state) => get(state, 'fit.stepInputsById')
export const stepIsSkippedById = (state) => get(state, 'fit.stepIsSkippedById')
export const stepResponsesById = (state) => get(state, 'fit.stepResponsesById')
export const surveyInputFailure = (state) => get(state, 'fit.surveyInputFailure')
export const thereWasASuccessfulCalculationDuringThisSession = (state) =>
  get(state, 'fit.calculate.thereWasASuccessfulCalculationDuringThisSession')

// Computed

export const errorIsVisible = (state) => !!get(state, 'fit.errorIsVisible')

export const infoIsVisible = (state) => !!get(state, 'fit.infoIsVisible')

export const hasSurveyInputFailure = createSelector(surveyInputFailure, isFilledString)

export const {isCalculateFailure, isCalculatePending, isCalculateSuccess} = status(
  'fit.calculate',
  'isCalculate',
)

export const onDeckStep = createSelector(flow, onDeckStepSlug, findBySlug)

export const currStep = createSelector(flow, currStepSlug, findBySlug)

export const oldStep = createSelector(flow, oldStepSlug, findBySlug)

export const onDeckStepId = createSelector(onDeckStep, getStepId)

export const currStepId = createSelector(currStep, getStepId)

export const oldStepId = createSelector(oldStep, getStepId)

export const currStepIsInSurvey = createSelector(currStep, stepIsInSurvey)

export const currStepIsRequired = createSelector(currStep, stepIsRequired)

export const currStepFlowIndex = createSelector(flow, currStep, indexOf)

export const oldStepFlowIndex = createSelector(flow, oldStep, indexOf)

export const forwards = createSelector(
  currStepFlowIndex,
  oldStepFlowIndex,
  (currIndex, oldIndex) => oldIndex < currIndex,
)

export const nextStep = createSelector(flow, currStepFlowIndex, (steps, index) =>
  get(steps, index + 1),
)

export const nextStepUrl = createSelector(nextStep, getStepUrl)

export const prevStep = createSelector(flow, currStepFlowIndex, (steps, index) =>
  get(steps, index - 1),
)

export const prevStepUrl = createSelector(prevStep, getStepUrl)

export const currStepInput = createSelector(stepInputsById, currStepId, get)

export const currStepHasInput = createSelector(currStepInput, isFilledString)

export const allSurveySteps = createSelector(flow, filterSurveySteps)

export const allRequiredSurveySteps = createSelector(allSurveySteps, filterRequiredSteps)

// always returns a number, which means that if we're before the survey, it returns 0;
//  if we're after the survey, it returns the count of the survey
export const currStepSoftSurveyCount = createSelector(
  flow,
  currStep,
  (steps, curr) => filterSurveySteps(slice(steps, 0, indexOf(steps, curr) + 1)).length,
)

export const currStepSoftSurveyIndex = createSelector(currStepSoftSurveyCount, (count) => count - 1)

export const surveyStepsCount = createSelector(allSurveySteps, size)

export const percentCompleteSurvey = createSelector(
  currStepSoftSurveyCount,
  surveyStepsCount,
  divide,
)

export const finalSurveyStep = createSelector(allSurveySteps, surveyStepsCount, (steps, total) =>
  get(steps, total - 1),
)

export const currStepIsFinalSurveyStep = createSelector(
  currStep,
  finalSurveyStep,
  (curr, final) => curr === final,
)

export const allRequiredSurveyStepsAreComplete = createSelector(
  allRequiredSurveySteps,
  stepResponsesById,
  stepsHaveValidResponses,
)

export const furthestAcceptableSurveyStepUrl = createSelector(
  allSurveySteps,
  stepResponsesById,
  finalSurveyStep,
  (steps, responses, finalStep) =>
    getStepUrl(
      find(steps, (step) => stepIsRequired(step) && !stepHasValidResponse(step, responses)) ||
        finalStep,
    ),
)

// Args

export const getStepBySlug = (state, slug) => findBySlug(flow(state), slug)

export const getStepById = (state, id) => findById(flow(state), id)

export const getStepInputById = (state, id) => get(stepInputsById(state), id)

export const getStepIsSkippedById = (state, id) => !!get(stepIsSkippedById(state), id)

export const getStepResponseById = (state, id) => get(stepResponsesById(state), id)

export const getStepSummaryResponseForStepById = (state, id) =>
  ((response) =>
    response ? getStepById(state, id).type.keyToObject(response).toSummaryLabel() : '—')(
    getStepResponseById(state, id),
  )

export const isSelectedResponse = (state, step, value) =>
  getStepResponseById(state, getStepId(step)) === value

export const stepIsSkipped = (state, step) =>
  (fitProfile.exist(state) && !stepHasValidResponse(step, stepResponsesById(state))) ||
  getStepIsSkippedById(state, getStepId(step))

export const slugExists = (state, slug) => hasSlug(flow(state), slug)

export const hasSurveyResponsesUpTo = (state, step) => {
  const steps = allSurveySteps(state)
  const prevSteps = slice(steps, 0, indexOf(steps, step))
  const prevReqSteps = filterRequiredSteps(prevSteps)
  return stepsHaveValidResponses(prevReqSteps, stepResponsesById(state))
}
